import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import LocuLogo from '../components/LocuLogo';
import SEO from '../components/SEO';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Terms of Service | LOCU"
        description="Read LOCU's Terms of Service. Learn about account terms, points earning and redemption policies, user responsibilities, and service guidelines."
        path="/terms"
      />
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <LocuLogo showText={true} textSize="text-xl" textColor="text-gray-900" />
          </Link>
          <Link
            to="/"
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Home
          </Link>
        </div>

        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          <p className="text-gray-500 mb-8">Last updated: January 2025</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing or using LOCU's services, you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Eligibility</h2>
            <p className="text-gray-700 mb-4">
              To use LOCU, you must:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Be at least 18 years old</li>
              <li>Be a resident of India</li>
              <li>Have a valid phone number and email address</li>
              <li>Be currently paying rent for a residential property</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. LOCU Points Program</h2>
            <p className="text-gray-700 mb-4">
              LOCU Points are rewards earned on eligible rent payments and other activities:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Points are earned at the rate of 2% on verified rent payments</li>
              <li>Points have a value of ₹1 per point when redeemed</li>
              <li>Points are non-transferable and cannot be exchanged for cash</li>
              <li>Points expire 12 months from the date of earning if not redeemed</li>
              <li>LOCU reserves the right to modify the points program at any time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Verification</h2>
            <p className="text-gray-700 mb-4">
              To earn LOCU Points on rent payments, you must:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Provide valid rent payment proof (receipts, bank statements, etc.)</li>
              <li>Complete identity verification when required</li>
              <li>Maintain accurate and up-to-date profile information</li>
            </ul>
            <p className="text-gray-700 mb-4">
              LOCU reserves the right to reject any submission that appears fraudulent or suspicious.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. User Conduct</h2>
            <p className="text-gray-700 mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Submit false or fraudulent information</li>
              <li>Attempt to manipulate the points system</li>
              <li>Create multiple accounts</li>
              <li>Use the service for any illegal purpose</li>
              <li>Harass or abuse other users or partner merchants</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Termination</h2>
            <p className="text-gray-700 mb-4">
              LOCU reserves the right to suspend or terminate your account if you violate these terms 
              or engage in fraudulent activity. Upon termination, any unused points may be forfeited.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              LOCU is not liable for any indirect, incidental, or consequential damages arising from 
              your use of the service. Our total liability shall not exceed the amount of points 
              redeemed by you in the 12 months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We may update these terms from time to time. We will notify you of any significant 
              changes via email or through the app. Continued use of the service after changes 
              constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms of Service, please contact us at{' '}
              <a href="mailto:support@locu.in" className="text-primary-600 hover:underline">
                support@locu.in
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;
